<template>
  <div class="checkout">
    <div class="checkout_header">
      <button
        class="close-btn"
        icon
        @click="$emit('close-drawer')"
      >
        <v-icon color="reddish">
          mdi-close
        </v-icon>
      </button>
      <h1 class="checkout-title">
        {{ $t('checkout.txt.title') }}
      </h1>
    </div>

    <div
      :key="`A-${y}`"
      class="checkout_main"
    >
      <div
        v-if="loadingCart"
        class="text-center"
      >
        <v-progress-circular
          class="loader"
          :size="30"
          color="peacock"
          indeterminate
        />
      </div>

      <template v-else>
        <div
          v-if="checkoutServices.length === 0 && !loadingCart"
          class="text-center pa-8"
        >
          <v-icon
            size="64"
            color="grey"
          >
            mdi-cart-outline
          </v-icon>
          <p class="mt-4">
            {{ $t('checkout.txt.empty_cart') || 'Your cart is empty' }}
          </p>
        </div>
        <v-expansion-panels
          v-else
          focusable
          flat
        >
          <v-expansion-panel
            v-for="service in checkoutServices"
            :key="service.id"
            class="service-panel"
            active-class="active"
          >
            <v-expansion-panel-header class="service-panel-header">
              <v-row v-if="service.name">
                <v-col
                  v-if="service"
                  cols="12"
                  sm="6"
                >
                  <v-img
                    v-if="service.iconUrl"
                    :src="service.iconUrl"
                    class="service-img"
                  />
                  <div class="service-info">
                    <div class="service-label">
                      {{ $t(service.typeLabel) }}
                    </div>
                    <div class="service-name">
                      {{ service.name }}
                    </div>
                  </div>
                </v-col>
                <v-col
                  v-if="service.name"
                  cols="12"
                  sm="6"
                  class="text-sm-right pr-8"
                >
                  <div class="service-resume">
                    <div
                      v-if="service.vehicles.length >0"
                      class="service-vehicles"
                    >
                      {{ service.vehicles.length }} {{ $t('checkout.txt.vehicles_selected') }}
                    </div>
                    <div
                      v-if="service.totalMonthlyService != undefined || service.totalYearlyService != undefined"
                      class="service-totals"
                    >
                      <span v-if="service.totalMonthlyService != 0">
                        <b><span v-if="service.totalMonthlyService < 0">-</span> {{ Math.abs(service.totalMonthlyService) | formatPrice(currency.symbol) }}</b> {{ $t('checkout.txt.month') }}
                      </span>
                      <span v-if="service.totalYearlyService != 0 && service.totalMonthlyService != 0"> & </span>
                      <span v-if="service.totalYearlyService != 0">
                        <b><span v-if="service.totalYearlyService < 0">-</span> {{ Math.abs(service.totalYearlyService) | formatPrice(currency.symbol) }}</b> {{ $t('checkout.txt.year') }}
                      </span>
                    </div>
                  </div>
                </v-col>
              </v-row>
              <v-row v-else>
                <v-col>
                  <div class="service-info">
                    <div class="remove-title-header">
                      <span v-if="service.id==='scrapped'">{{ $t('checkout.txt.remove_vehicles_scrapped') }}</span>
                      <span v-else>{{ $t('checkout.txt.remove_vehicles_sold') }}</span>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-expansion-panel-header>
            <v-expansion-panel-content class="service-panel-content">
              <div v-if="service.name">
                <h2 v-if="service.vehicles.length >0">
                  {{ $t('checkout.txt.result_selected') }} {{ service.vehicles.length }} {{ $t('checkout.txt.result_vehicles') }}
                </h2>
                <div
                  v-if="service.vehicles.length >0"
                  class="options d-sm-none py-3"
                >
                  <span class="sort-by-label mobile">{{ $t('checkout.txt.sort_by') }}: </span>
                  <v-select
                    class="d-inline-flex sort-by-items mobile"
                    background-color="pale-three"
                    color="slate"
                    :append-icon="'mdi-chevron-down'"
                    :items="sortItems"
                    :menu-props="{ offsetY: true }"
                    :placeholder="$t('checkout.txt.period')"
                    dense
                    filled
                    rounded
                    return-object
                    @change="sortByItem"
                  />
                </div>
                <div
                  v-if="service.vehicles.length >0"
                  class="options"
                >
                  <div class="start-spacer" />
                  <a
                    v-if="allSelected"
                    @click="selectedTrucks = null; allSelected = !allSelected"
                  > {{ $t('checkout.txt.unselect_all') }} </a>
                  <a
                    v-else
                    @click="selectedTrucks=service.vehicles; allSelected = !allSelected"
                  > {{ $t('checkout.txt.select_all') }} </a>
                  <div class="vertical-spacer" />
                  <a @click="deleteItem(selectedTrucks, service)">{{ $t('checkout.txt.delete') }} </a>
                  <div class="horizontal-spacer" />
                  <span class="d-none d-sm-block sort-by-label">{{ $t('checkout.txt.sort_by') }}: </span>
                  <v-select
                    class="d-none d-sm-inline-flex sort-by-items"
                    background-color="pale-three"
                    color="slate"
                    :append-icon="'mdi-chevron-down'"
                    :items="sortItems"
                    :menu-props="{ offsetY: true }"
                    :placeholder="$t('checkout.txt.period')"
                    dense
                    filled
                    rounded
                    return-object
                    @change="sortByItem"
                  />
                </div>
              </div>
              <div v-else>
                <div class="confirm-question">
                  {{ $t('checkout.txt.confirm_question') }}
                </div>
              </div>
              <v-data-table
                v-if="service.vehicles.length >0"
                v-model="selectedTrucks"
                :items="service.vehicles"
                item-key="id"
                :items-per-page="numberItems"
                :sort-by="sortBy"
                :hide-default-footer="true"
                class="service-table"
              >
                <template v-slot:body="{ items }">
                  <div
                    v-for="item in items"
                    :key="item.id"
                    class="service-table-row"
                    :class="{ 'unsubscribe': !item.addSubscription }"
                  >
                    <v-row
                      v-if="item.ownerName"
                      class="mb-2"
                    >
                      <v-col
                        cols="2"
                        :md="'2'"
                        class="align-self-center flex"
                      >
                        <div class="table-header">
                          OWNER NAME:
                        </div>
                      </v-col>
                      <v-col
                        cols="6"
                        :md="'6'"
                        class="align-self-center flex"
                      >
                        <div>{{ item.ownerName }}</div>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col
                        cols="1"
                        sm="1"
                        class="align-self-center"
                      >
                        <v-checkbox
                          v-model="selectedTrucks"
                          :value="item"
                          hide-details
                          :ripple="false"
                          dense
                        />
                      </v-col>
                      <v-col
                        cols="1"
                        class="align-self-center d-none d-md-inline"
                      >
                        <v-img
                          v-if="item.vehicleType!==null"
                          :src="$t('trucks.img.'+item.vehicleType+'.img')"
                          width="32"
                        />
                      </v-col>
                      <v-col
                        cols="2"
                        :class="service.name ? 'align-self-center d-none d-md-inline' : 'align-self-center d-none d-md-inline pl-4'"
                      >
                        <div class="table-header">
                          {{ headers[0].text }}
                        </div>
                        <div>
                          {{ item.vehicleType }}
                        </div>
                      </v-col>
                      <v-col
                        :cols="service.name ? '3' : '4'"
                        :md="service.name ? '3' : '4'"
                        class="align-self-center d-none d-sm-inline"
                      >
                        <div class="table-header">
                          {{ headers[1].text }}
                        </div>
                        <div v-if="item.vinNumber">
                          <v-tooltip
                            content-class="vehiclevin-tooltip"
                            bottom
                            color="#4a5568"
                          >
                            <template v-slot:activator="{ on }">
                              <span v-on="on">...{{ item.vinNumber.substring(item.vinNumber.length - 8) }}</span>
                            </template>
                            <div>
                              <div>
                                <span class="vehiclevin-span"> {{ $t('vehicles_overview.txt.vehicle_name') }}: </span>{{ item.vehicleName }}
                              </div>
                              <div>
                                <span class="vehiclevin-span">{{ $t('vehicles_overview.txt.vin_number') }}: </span>{{ item.vinNumber }}
                              </div>
                            </div>
                          </v-tooltip>
                        </div>
                      </v-col>
                      <v-col
                        cols="4"
                        :md="service.name ? '3' : '4'"
                        class="align-self-center"
                      >
                        <div class="table-header">
                          {{ headers[2].text }}
                        </div>
                        <div>
                          {{ item.licensePlate }}
                        </div>
                      </v-col>
                      <v-col
                        v-if="service.name"
                        cols="6"
                        md="2"
                        class="align-self-center"
                      >
                        <div class="table-header" />
                        <div
                          class="row-total"
                          :class="{ 'unsubscribe': !item.addSubscription }"
                        >
                          <span v-if="!item.addSubscription">-</span> {{ item.cost | formatPrice(currency.symbol) }} <span v-if="item.period === 'month'"> {{ $t('checkout.txt.month') }}</span>
                          <span v-if="item.period === 'year'"> {{ $t('checkout.txt.year') }}</span>
                        </div>
                      </v-col>
                    </v-row>

                    <div
                      v-if="!item.addSubscription"
                      class="data-end-message"
                    >
                      {{ $t('checkout.txt.service_end_msg') }} {{ item.endDate | formatDate }}
                    </div>
                  </div>
                </template>
              </v-data-table>
              <div
                v-else
                class="service-table-row"
              >
                <h2>{{ service.name }}</h2>
                <div
                  class="service-description"
                  v-html="service.description"
                />
              </div>
              <div class="show-all">
                <v-btn
                  v-if="service.vehicles.length > 2 && numberItems < service.vehicles.length"
                  elevation="0"
                  @click="numberItems = service.vehicles.length"
                >
                  {{ $t('checkout.txt.show_all') }} ({{ service.vehicles.length }})
                </v-btn>
                <v-btn
                  v-if="numberItems === service.vehicles.length && service.vehicles.length > 2"
                  elevation="0"
                  @click="numberItems = 2"
                >
                  {{ $t('checkout.txt.show_less_items') }}
                </v-btn>
              </div>
              <v-row v-if="!service.name">
                <v-col
                  cols="12"
                  sm="3"
                  md="3"
                  class="text-left text-sm-left ml-3 mt-8"
                >
                  <span class="total-label">{{ $t('checkout.txt.date_removal') }}:</span>
                </v-col>
                <v-col
                  cols="12"
                  sm="3"
                  md="3"
                  class="text-center text-sm-center ml-n4 mt-6"
                >
                  <v-select
                    v-model="day"
                    :value="day"
                    class="select mx-0"
                    :items="days"
                    :placeholder="$t('checkout.txt.day')"
                    append-icon="mdi-chevron-down"
                    :menu-props="{ offsetY: true }"
                    background-color="pale-two"
                    filled
                    rounded
                    dense
                    lazy
                    @change="updateVehicleToRemove(service)"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="3"
                  md="3"
                  class="text-center text-sm-center ml-n2 mt-6 "
                >
                  <v-select
                    v-model="month"
                    :value="month"
                    class="select mx-0"
                    :items="months"
                    item-text="name"
                    item-value="number"
                    :placeholder="$t('checkout.txt.month')"
                    append-icon="mdi-chevron-down"
                    :menu-props="{ offsetY: true }"
                    background-color="pale-two"
                    filled
                    rounded
                    dense
                    lazy
                    @change="updateVehicleToRemove(service)"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="3"
                  md="3"
                  class="text-center text-sm-center ml-n2 mt-6"
                >
                  <v-select
                    v-model="year"
                    :value="year"
                    class="select mx-0"
                    :items="years"
                    :placeholder="$t('checkout.txt.year')"
                    append-icon="mdi-chevron-down"
                    :menu-props="{ offsetY: true }"
                    background-color="pale-two"
                    filled
                    rounded
                    dense
                    lazy
                    @change="updateVehicleToRemove(service)"
                  />
                </v-col>
                <v-col
                  v-if="service.id==='changeOwner'"
                  cols="12"
                  sm="3"
                  md="3"
                  class="text-left text-sm-left ml-3"
                >
                  <span class="total-label">{{ $t('checkout.txt.new_owner') }}:</span>
                </v-col>
                <v-col
                  v-if="service.id==='changeOwner'"
                  cols="12"
                  sm="9"
                  md="9"
                  class="text-center text-sm-center ml-n4 pr-7"
                >
                  <v-text-field
                    v-model="newOwner"
                    class="field-change-owner py-0"
                    :placeholder="$t('checkout.txt.new_owner_ph')"
                    maxlength="255"
                    background-color="pale-two"
                    filled
                    rounded
                    dense
                    lazy
                    @change="updateVehicleToRemove(service)"
                  />
                </v-col>
              </v-row>
              <!-- <div v-if="service.id==='changeOwner'">
                <v-col
                  cols="12"
                  sm="3"
                  md="3"
                  class="text-left text-sm-left ml-3 mt-8"
                >
                {{ service.id }}
                <span class="total-label">{{ $t('checkout.txt.new_owner') }}:</span>
              </v-col>
              </div> -->
              <div class="total">
                <v-row>
                  <v-col
                    cols="12"
                    sm="6"
                    class="align-self-end text-center text-sm-left"
                  >
                    <v-icon
                      color="cloudy"
                      dense
                    >
                      mdi-delete
                    </v-icon>
                    <v-btn
                      v-if="service.name"
                      text
                      small
                      color="dark"
                      class="delete-order"
                      @click="deleteOrder"
                    >
                      {{ $t('checkout.txt.delete_order') }}
                    </v-btn>
                    <v-btn
                      v-else
                      text
                      small
                      color="dark"
                      class="undo-removal"
                      @click="deleteItem(selectedTrucks, service)"
                    >
                      {{ $t('checkout.txt.undo_removal') }}
                    </v-btn>
                  </v-col>
                  <v-col
                    v-if="service.name"
                    cols="12"
                    sm="6"
                    class="text-center text-sm-right"
                  >
                    <span
                      v-if="service.totalMonthlyService != 0 || service.totalYearlyService != 0"
                      class="total-label"
                    >{{ $t('checkout.txt.total') }}:</span>
                    <span
                      v-if="service.totalMonthlyService != 0"
                      class="total-value"
                    >
                      <b><span v-if="service.totalMonthlyService < 0 && service.totalMonthlyService != 0">-</span> {{ Math.abs(service.totalMonthlyService) | formatPrice(currency.symbol) }}</b> <span class="total-value-label">{{ $t('checkout.txt.month') }}</span>
                    </span>
                    <span v-if="service.totalYearlyService != 0 && service.totalMonthlyService != 0"> & </span>
                    <span
                      v-if="service.totalYearlyService != 0"
                      class="total-value"
                    >
                      <b><span v-if="service.totalYearlyService < 0">-</span> {{ Math.abs(service.totalYearlyService) | formatPrice(currency.symbol) }}</b> <span class="total-value-label">{{ $t('checkout.txt.year') }}</span>
                    </span>
                  </v-col>
                </v-row>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <template v-if="checkoutServices.length">
          <div v-if="isCddChecked && isAddSubscription">
            <div v-if="customTermsList.length">
              <div
                v-for="customTerm in customTermsList"
                :key="customTerm.id"
                class="terms"
              >
                <div v-html="customTerm.termsText" />
                <v-checkbox
                  v-model="customTerm.confirmed"
                  hide-details
                  class="d-inline-block check-terms"
                  :ripple="false"
                  value=""
                />
                <span
                  class="d-inline-block"
                  v-html="customTerm.confirmText"
                />
              </div>
            </div>
            <div
              v-if="showBaseTerms"
              class="terms"
            >
              <div v-html="$t('checkout.txt.terms_text')" />
              <v-checkbox
                v-model="checkTerms"
                hide-details
                class="d-inline-block check-terms"
                :ripple="false"
                value=""
              />
              <span
                class="d-inline-block"
                v-html="$t('checkout.txt.read_terms')"
              />
            </div>
          </div>
          <v-alert
            v-if="accountInformation && Object.keys(accountInformation).length > 0 && !isCddChecked"
            text
            prominent
            color="reddish"
            class="mt-8"
          >
            <label class="alert-cdd-check">{{ $t('checkout.txt.cdd_check_alert') }}</label>
          </v-alert>
        </template>
      </template>
    </div>

    <v-footer
      :key="`B-${y}`"
      class="checkout_footer"
      absolute
    >
      <v-row>
        <v-col
          cols="12"
          md="6"
          class="text-right text-md-left"
        >
          <div v-if="y==totaltruck">
            <div
              v-if="totalMonthly != 0 || totalYearly != 0"
              class="total-price"
            >
              {{ $t('checkout.txt.total_price') }}
            </div>
            <span v-if="totalMonthly != 0">
              <b><span v-if="totalMonthly < 0">-</span> {{ Math.abs(totalMonthly) | formatPrice(currency.symbol) }}</b> {{ $t('checkout.txt.month') }}
            </span>
            <span v-if="totalYearly != 0 && totalMonthly != 0"> & </span>
            <span v-if="totalYearly != 0">
              <b><span v-if="totalYearly < 0">-</span> {{ Math.abs(totalYearly) | formatPrice(currency.symbol) }}</b> {{ $t('checkout.txt.year') }}
            </span>
          </div>
        </v-col>
        <v-col
          cols="12"
          md="6"
          class="align-self-center"
        >
          <v-btn
            large
            dark
            right
            color="peacock"
            elevation="0"
            class="float-right btn-pay"
            :disabled="!isCddChecked || (isAddSubscription && showBaseTerms &&!checkTerms) || (isAddSubscription && !customTermsChecked)"
            :loading="loading"
            @click="goToPayment"
          >
            {{ $t('checkout.txt.update_subscription') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-footer>
  </div>
</template>

<script>
import apiCalls from '@/helpers/apiCall'
import api from '@/helpers/api'
import { mapState, mapMutations, mapActions } from 'vuex'
import prismicService from '@/helpers/prismicService'
import moment from 'moment'
import { ProductType } from '@/helpers/enums'

export default {
  name: 'AppCheckout',

  data() {
    return {
      services: [],
      sortItems: [],
      itemIndex: 0,
      lessTrucks: [],
      selectedTrucks: [],
      headers: [],
      checkoutTrucks: [],
      checkoutServices: [],
      checkTerms: false,
      checkAllVehicles: [],
      numberItems: 2,
      sortBy: '',
      y: 0,
      totaltruck: 0,
      isAddSubscription: true,
      allSelected: false,
      notification: false,
      notificationText: '',
      notificationType: 'success',
      newOwner: '',
      day: '',
      month: '',
      year: '',
      monthsWith30Days: ['04', '06', '09', '11'],
      months: [
        {
          name: 'January',
          number: '01'
        },
        {
          name: 'February',
          number: '02'
        },
        {
          name: 'March',
          number: '03'
        },
        {
          name: 'April',
          number: '04'
        },
        {
          name: 'May',
          number: '05'
        },
        {
          name: 'June',
          number: '06'
        },
        {
          name: 'July',
          number: '07'
        },
        {
          name: 'August',
          number: '08'
        },
        {
          name: 'September',
          number: '09'
        },
        {
          name: 'October',
          number: '10'
        },
        {
          name: 'November',
          number: '11'
        },
        {
          name: 'December',
          number: '12'
        }
      ],
      loading: false,
      loadingCart: false,
      authorized: localStorage.getItem('authorized'),
      customTermsList: [],
      showBaseTerms: true
    }
  },

  computed: {
    ...mapState('checkout', ['totalCheckout', 'checkout']),
    ...mapState('account', ['currency', 'accountInformation', 'detailsLoaded']),
    ...mapState('trucks', ['trucks']),

    isCddChecked() {
      // Verificar se accountInformation existe (mesmo que detailsLoaded seja false, pode ter sido carregado)
      if (!this.accountInformation || Object.keys(this.accountInformation).length === 0) {
        return false
      }
      // Verificar se CddCheck existe e é 'Approved'
      const cddCheck = this.accountInformation.CddCheck

      // Verificar valores falsy: null, undefined, '', 0, false
      if (cddCheck === null || cddCheck === undefined || cddCheck === '') {
        return false
      }

      // Comparação robusta: remove espaços e compara case-insensitive
      const normalizedCddCheck = String(cddCheck).trim().toLowerCase()
      const isApproved = normalizedCddCheck === 'approved'

      return isApproved
    },
    totalMonthly() {
      const totalMonthly = this.checkoutServices.reduce(
        (prev, cur) => prev + parseFloat(cur.totalMonthlyService),
        0
      )
      return totalMonthly || 0
    },
    totalYearly() {
      const totalYearly = this.checkoutServices.reduce(
        (prev, cur) => prev + parseFloat(cur.totalYearlyService),
        0
      )
      return totalYearly || 0
    },
    years: function() {
      const year = []
      const currentYear = new Date().getFullYear()
      for (let i = 1960; i <= currentYear; ++i) {
        year.push({ text: i, value: i.toString() })
      }
      return year
    },
    days: function() {
      const day = []
      let lastday
      let d

      if (this.monthsWith30Days.includes(this.month)) {
        lastday = 30
      } else if (this.leapYear(this.year)) {
        lastday = 29
      }
      if (this.month === '02' && !this.leapYear(this.year)) {
        lastday = 28
      } else {
        lastday = 31
      }
      for (let i = 1; i <= lastday; ++i) {
        if (i <= 9) d = '0' + i
        else d = i
        day.push({ text: d, value: d.toString() })
      }

      return day
    },
    customTermsChecked() {
      const checked =
        this.customTermsList.length == 0 ||
        (this.customTermsList.length > 0 &&
          this.customTermsList.every(t => t.confirmed))
      return checked
    }
  },

  watch: {
    accountInformation: {
      handler() {
        // Forçar atualização do componente para garantir reatividade quando accountInformation mudar
        this.$nextTick(() => {
          this.$forceUpdate()
        })
      },
      deep: true,
      immediate: true
    }
  },

  async created() {
    this.loadingCart = true
    // Carregar accountDetails apenas se não existir ou estiver vazio
    // Não forçar refresh desnecessário - usar cache quando disponível
    if (!this.detailsLoaded || !this.accountInformation || Object.keys(this.accountInformation).length === 0) {
      await this.getAccountDetails()
    } else if (!this.isCddChecked) {
      // DAF-650: o CDD pode ter sido aprovado no Zuora depois do boot, deixando o
      // store com um valor obsoleto. Fura o cache local de 60s antes de renderizar
      // o gate. Anti-loop: o else-if garante no máximo 1 refresh forçado por mount,
      // e o componente só é montado quando o drawer do checkout abre.
      await this.getAccountDetails(true).catch(() => {
        // se o refresh falhar, seguir com o valor do store (comportamento pré-fix)
      })
    }
    // Carregar serviços primeiro - necessário para processar checkout
    await this.getServices()
    await this.getSortItems()
    await this.getHeaders()
    // getVehicles() agora tem cache - não será redundante se já carregado
    await this.getVehicles()
    // Sempre processar checkout para garantir que checkoutServices seja populado
    // Mesmo que checkout exista no store, precisa processar para popular checkoutServices
    await this.getCheckout()
    await this.prepareCustomTerms()
    this.loadingCart = false
  },

  methods: {
    ...mapMutations('checkout', [
      'setTotalCheckout',
      'removeFromTotalCheckout',
      'decrement',
      'setCheckout'
    ]),
    ...mapMutations('trucks', ['setBillingPeriod']),
    ...mapActions('trucks', ['getVehicles', 'invalidateTrucksCache']),
    ...mapActions('notification', ['setSnackbar']),
    ...mapActions('account', ['getAccountDetails']),
    ...mapActions('checkout', ['getCheckout']),
    leapYear: function(year) {
      return (year % 4 == 0 && year % 100 != 0) || year % 400 == 0
    },
    async getServices() {
      // Usar store com cache para evitar chamadas redundantes
      const products = await this.$store.dispatch('products/getAllProducts')
      this.services = products
      return Promise.resolve(products)
        .then(() => {
          return prismicService.getProductCatalog().then(catalog => {
            this.services.forEach(serv => {
              catalog.forEach(catalogProduct => {
                if (catalogProduct.externalKey === serv.sku) {
                  serv.name = catalogProduct.title
                  serv.iconUrl = catalogProduct.iconActiveUrl
                  serv.description = catalogProduct.description
                  serv.typeLabel =
                    serv.type__c == ProductType.InVehicleApp
                      ? 'checkout.txt.vehicle_app'
                      : 'checkout.txt.service'
                  serv.productRatePlans.forEach(product => {
                    //only monthly or yearly plans
                    if (
                      product.billingPeriod__c != 'Annual' &&
                      product.billingPeriod__c != 'Monthly'
                    ) {
                      return true
                    }
                    product.productRatePlanCharges.forEach(bp => {
                      if (bp.billingPeriod === 'Annual') {
                        serv.ratePlanIdYearly = product.id
                        serv.ratePlanChargesIdYearly = bp.id
                        bp.pricing.forEach(pr => {
                          if (pr.currency === this.currency.code)
                            serv.yearlyCost = pr.price
                        })
                      } else if (bp.billingPeriod === 'Month') {
                        serv.ratePlanIdMonthly = product.id
                        serv.ratePlanChargesIdMonthly = bp.id
                        bp.pricing.forEach(pr => {
                          if (pr.currency === this.currency.code)
                            serv.monthlyCost = pr.price
                        })
                      }
                    })
                  })
                }
              })
              return serv
            })
          })
        })
    },
    getEndDate(truckSubscriptionID, productRatePlanChargeID, period, service) {
      let chargedThroughDate = ''
      // * Selects the truck based on the Subscription ID/Number
      const truck = this.trucks.find(truck => {
        return truck.subscriptionNumber == truckSubscriptionID
      })
      // * Searches the selected truck rateplans for the correct productRatePlanChargeID
      truck.ratePlans.forEach(rateplan => {
        rateplan.ratePlanCharges.forEach(rateplancharge => {
          if (
            rateplancharge.productRatePlanChargeId == productRatePlanChargeID
          ) {
            // * When the correct ratePlanCharge is found, gets the chargedThroughDate and effectiveEndDate
            chargedThroughDate = rateplancharge.chargedThroughDate
          }
        })
      })
      let endDate = ''
      // * If is a Vehicle Apps is stops on the day the action takes place
      if (service.type__c === ProductType.InVehicleApp) {
        endDate = moment().format('YYYY-MM-DD')
      } else {
        // * If there isn't a chargedThroughDate gets today and sets the chargedThroughDate to be the 1st day of the next valid month (must have at least 30 days between today and chargedThroughDate).
        if (!chargedThroughDate) {
          chargedThroughDate = moment().format('YYYY-MM-DD')
          const day = moment(chargedThroughDate).date()
          const endYear = moment(chargedThroughDate).year()
          const currentYear = moment().year()

          if (day == 1)
            endDate = moment(chargedThroughDate)
              .add(1, 'month')
              .subtract(endYear - currentYear, 'years')
              .format('YYYY-MM-01')
          else
            endDate = moment(chargedThroughDate)
              .add(2, 'month')
              .subtract(endYear - currentYear, 'years')
              .format('YYYY-MM-01')
          // }
        }
        // * If there is a chargedThroughDate, it'll be correct from BE, so just returns it.
        endDate = chargedThroughDate
      }
      return endDate
    },
    async getCheckout() {
      this.checkoutServices = []
      let products = null

      // Usar dados do store se disponíveis, senão buscar da API
      const checkoutData = this.checkout && this.checkout.length > 0
        ? this.checkout
        : null

      if (checkoutData) {
        // Processar dados do store diretamente - evita chamada redundante à API
        products = checkoutData
      } else {
        // Se não houver dados no store, buscar da API e atualizar store
        const res = await apiCalls.get(api.CartGetAll, '', '')
        products = res.data.products || []
        this.setCheckout(products)
      }

      // Sempre processar os dados para popular checkoutServices
      if (products && products.length > 0) {
        await this.processCheckoutData(products)
      }
    },
    async processCheckoutData(products) {
      let totalMonthly = 0
      let totalYearly = 0
      this.checkoutServices = []
      this.checkout = products
      const x = []
      const allPromises = []

      // Verificar se services está carregado - necessário para processar
      if (!this.services || this.services.length === 0) {
        console.warn('AppCheckout: services não carregado, não é possível processar checkout')
        return Promise.resolve()
      }

      if (products && products.length > 0) {
        products.forEach(i => {
            this.services.forEach(service => {
              if (i.productID == service.id) {
                // Shallow copy to avoid mutating the shared service object in this.services
                const auxService = { ...service }
                const auxVehicles = []
                let sumMonthly = 0
                let sumYearly = 0
                this.totaltruck += i.rateplans.length
                i.rateplans.forEach(rateplan => {
                  rateplan.subscriptions.forEach(truck => {
                    if (truck.addSubscription) {
                      this.isAddSubscription = true
                    } else {
                      this.isAddSubscription = false
                    }
                    if (truck.subscriptionID) {
                      const p = apiCalls
                        .get(api.TrucksGet, truck.subscriptionID, '')
                        .then(res => {
                          if (
                            truck.productRateplanChargeID ===
                            service.ratePlanChargesIdYearly
                          ) {
                            res.data.cost = service.yearlyCost || 0
                            res.data.period = 'year'
                            if (truck.addSubscription) {
                              sumYearly += res.data.cost
                              totalYearly += res.data.cost
                            } else {
                              sumYearly -= res.data.cost
                              totalYearly -= res.data.cost
                              res.data.endDate = this.getEndDate(
                                truck.subscriptionID,
                                service.ratePlanChargesIdYearly,
                                res.data.period,
                                service
                              )
                            }
                            res.data.addSubscription = truck.addSubscription
                            auxVehicles.push(res.data)
                          } else if (
                            truck.productRateplanChargeID ===
                            service.ratePlanChargesIdMonthly
                          ) {
                            res.data.cost = service.monthlyCost || 0
                            res.data.period = 'month'
                            if (truck.addSubscription) {
                              sumMonthly += res.data.cost
                              totalMonthly += res.data.cost
                            } else {
                              sumMonthly -= res.data.cost
                              totalMonthly -= res.data.cost
                              res.data.endDate = this.getEndDate(
                                truck.subscriptionID,
                                service.ratePlanChargesIdMonthly,
                                res.data.period,
                                service
                              )
                            }
                            res.data.addSubscription = truck.addSubscription
                            auxVehicles.push(res.data)
                          }
                          auxService.totalMonthlyService = Number.isFinite(sumMonthly) ? sumMonthly : 0
                          auxService.totalYearlyService = Number.isFinite(sumYearly) ? sumYearly : 0
                          this.y += 1

                          x.totalMonthly = Number.isFinite(totalMonthly) ? totalMonthly : 0
                          x.totalYearly = Number.isFinite(totalYearly) ? totalYearly : 0
                        })
                      allPromises.push(p)
                    } else {
                      if (truck.addSubscription) {
                        if (
                          truck.productRateplanChargeID ===
                          service.ratePlanChargesIdYearly
                        ) {
                          auxService.cost = service.yearlyCost || 0
                          auxService.period = 'year'
                          sumYearly += service.yearlyCost
                          totalYearly += service.yearlyCost
                        } else if (
                          truck.productRateplanChargeID ===
                          service.ratePlanChargesIdMonthly
                        ) {
                          auxService.cost = service.monthlyCost || 0
                          auxService.period = 'month'
                          sumMonthly += service.monthlyCost
                          totalMonthly += service.monthlyCost
                        }
                      } else if (
                        rateplan.productRateplanID === service.ratePlanIdYearly
                      ) {
                        sumYearly += service.yearlyCost
                        totalYearly += service.yearlyCost
                      } else {
                        sumMonthly -= service.monthlyCost
                        totalMonthly -= service.monthlyCost
                      }

                      auxService.totalMonthlyService = sumMonthly || 0
                      auxService.totalYearlyService = sumYearly || 0
                      this.y += 1
                      x.totalMonthly = totalMonthly || 0
                      x.totalYearly = totalYearly || 0
                    }
                    auxService.vehicles = auxVehicles
                  })
                })
                x.push(auxService)
              }
            })
            if (i.productID === 'changeOwner' || i.productID === 'scrapped') {
              const auxService = {}
              const auxVehicles = []
              this.totaltruck += i.rateplans.length
              i.rateplans.forEach(rateplan => {
                rateplan.subscriptions.forEach(truck => {
                  if (truck.subscriptionID) {
                    const p = apiCalls
                      .get(api.TrucksGet, truck.subscriptionID, '')
                      .then(res => {
                        res.data.addSubscription = 'remove'
                        this.y += 1
                        auxVehicles.push(res.data)
                      })
                    allPromises.push(p)
                  }
                  auxService.id = i.productID
                  auxService.vehicles = auxVehicles
                  auxService.totalMonthlyService = 0
                  auxService.totalYearlyService = 0
                })
              })
              if (i.metaData) {
                const metaData = JSON.parse(i.metaData)
                let cancelationDate = new Date()
                cancelationDate = metaData.cancelationDate
                this.cancelationDate = cancelationDate
                this.newOwner = metaData.newOwner
                const arrayDate = cancelationDate.split('-')
                this.year = arrayDate[0]
                this.month = arrayDate[1]
                this.day = arrayDate[2]
              }

              x.push(auxService)
            }
          })
        }

      // Wait for all TrucksGet calls to complete before rendering so vehicle
      // details (type, VIN, registration, price) are available on first render.
      // Previously these ran fire-and-forget, causing Vue 2 to miss the updates
      // because auxVehicles was not reactive when checkoutServices was assigned.
      await Promise.all(allPromises)
      this.checkoutServices = x
      return Promise.resolve()
    },

    sortByItem(item) {
      this.sortBy = item.key
    },

    deleteItem(vehiclesSelected, service) {
      const array = []
      vehiclesSelected.forEach(vehicle => {
        this.checkout.forEach(product => {
          if (product.productID === service.id) {
            product.rateplans.forEach(rateplan => {
              rateplan.subscriptions.forEach(subscription => {
                if (
                  vehicle.subscription_number === subscription.subscriptionID
                ) {
                  if (subscription.addSubscription == true)
                    array.push({
                      subscriptionID: subscription.subscriptionID,
                      productRateplanID: rateplan.productRateplanID,
                      rateplanID: rateplan.rateplanID,
                      productRateplanChargeID:
                        subscription.productRateplanChargeID,
                      productID: product.productID,
                      addSubscription: 1,
                      metaData: { '': '' }
                    })
                  else if (
                    (product.productID === 'scrapped' ||
                      product.productID === 'changeOwner') &&
                    subscription.addSubscription == false
                  ) {
                    array.push({
                      subscriptionID: subscription.subscriptionID,
                      productRateplanID: rateplan.productRateplanID,
                      rateplanID: rateplan.rateplanID,
                      productRateplanChargeID:
                        subscription.productRateplanChargeID,
                      productID: product.productID,
                      addSubscription: false
                    })
                  } else
                    array.push({
                      subscriptionID: subscription.subscriptionID,
                      productRateplanID: rateplan.productRateplanID,
                      rateplanID: rateplan.rateplanID,
                      productRateplanChargeID:
                        subscription.productRateplanChargeID,
                      productID: product.productID,
                      addSubscription: 0,
                      metaData: { '': '' }
                    })
                }
              })
            })
          }
        })
      })
      array.forEach(item => {
        return apiCalls.delete(api.CartRemove, '', '', item).then(
          response => {
            if (response.status == 200) {
              this.$forceUpdate()
              this.fieldKey = Math.random()
              // Atualizar store diretamente com dados da resposta - não precisa chamar getCheckout() novamente
              this.setCheckout(response.data.products || [])
              this.invalidateTrucksCache()
              this.decrement()
              this.$emit('close-drawer')
              this.setSnackbar({
                showing: true,
                type: 'success',
                text: this.$t('checkout.txt.notification_delete_item')
              })
            } else if (response.status == 204) {
              this.$forceUpdate()
              this.fieldKey = Math.random()
              // Atualizar store diretamente com dados da resposta
              this.setCheckout(response.data.products || [])
              this.decrement()
              this.$emit('close-drawer')
              this.setSnackbar({
                showing: true,
                type: 'success',
                text: this.$t('checkout.txt.notification_delete_item')
              })
            }
          },
          error => {
            this.setSnackbar({
              showing: true,
              type: 'error',
              text: this.$t('checkout.txt.notification_error')
            })
            return Promise.reject(error)
          }
        )
      })
    },

    deleteOrder() {
      return apiCalls
        .post(api.CartClear, '', '', '')
        .then(response => {
          if (response.status == 200) {
            this.$forceUpdate()
            // Limpar checkout diretamente - não precisa chamar getCheckout() novamente
            this.setCheckout([])
            this.setTotalCheckout(0)
            this.invalidateTrucksCache()
            localStorage.setItem('billingPeriod', null)
          }
        })
        .then(
          () => {
            // Removida chamada redundante - já atualizamos o store acima
            // Se precisar recarregar, usar getCheckout(true) para forçar refresh
            this.$emit('close-drawer')
            this.setSnackbar({
              showing: true,
              type: 'success',
              text: this.$t('checkout.txt.notification_delete_order')
            })
          },
          error => {
            this.setSnackbar({
              showing: true,
              type: 'error',
              text: this.$t('checkout.txt.notification_error')
            })
            return Promise.reject(error)
          }
        )
    },
    goToPayment() {
      this.loading = true
      return apiCalls
        .post(api.CartSubmit, '', '', '')
        .then(response => {
          if (response.status == 200) {
            this.loading = false
            this.$forceUpdate()
            // Limpar checkout após submit - não precisa chamar getCheckout() novamente
            this.setCheckout([])
            this.setTotalCheckout(0)
            this.invalidateTrucksCache()
            localStorage.setItem('billingPeriod', null)
            this.setBillingPeriod([])
            this.$emit('close-drawer')

            let redirToAppLibrary = false

            if (
              response.data.cart.products &&
              response.data.cart.products.length > 0
            ) {
              this.setSnackbar({
                showing: true,
                type: 'error',
                text: this.$t('checkout.txt.notification_error')
              })
            } else {
              this.setSnackbar({
                showing: true,
                type: 'success',
                text: this.$t('checkout.txt.notification_submit_success')
              })

              redirToAppLibrary = response.data.hasNewAppPacks
            }

            if (redirToAppLibrary) {
              const url = this.$router.resolve({ name: 'appLibrary' }).href

              setTimeout(() => {
                location.assign(url)
              }, 1000)
            } else {
              setTimeout(() => {
                location.reload()
              }, 1000)
            }
          }
        })
        .catch(error => {
          console.error(error)
          this.loading = false
          this.setSnackbar({
            showing: true,
            type: 'error',
            text: this.$t('checkout.txt.notification_error')
          })
        })
    },
    getSortItems() {
      this.sortItems.push(
        { key: 'period', text: this.$t('checkout.txt.period') },
        { key: 'licensePlate', text: this.$t('checkout.txt.license_plate') },
        { key: 'vinNumber', text: this.$t('checkout.txt.vin_number') }
      )
    },
    getHeaders() {
      this.headers.push(
        { key: 'type', text: this.$t('checkout.txt.type') },
        { key: 'vinNumber', text: this.$t('checkout.txt.vin_number') },
        { key: 'licensePlate', text: this.$t('checkout.txt.license_plate') }
      )
    },
    updateVehicleToRemove(service) {
      this.cancelationDate = this.year + '-' + this.month + '-' + this.day
      this.checkout.forEach(product => {
        if (
          service.id === product.productID &&
          product.productID === 'scrapped'
        ) {
          product.rateplans.forEach(rateplan => {
            rateplan.subscriptions.forEach(subscription => {
              return apiCalls.put(api.CartUpdate, '', '', {
                subscriptionID: subscription.subscriptionID,
                productID: product.productID,
                metaData: { cancelationDate: this.cancelationDate }
              })
            })
          })
        } else if (
          service.id === product.productID &&
          product.productID === 'changeOwner'
        ) {
          product.rateplans.forEach(rateplan => {
            rateplan.subscriptions.forEach(subscription => {
              return apiCalls.put(api.CartUpdate, '', '', {
                subscriptionID: subscription.subscriptionID,
                productID: product.productID,
                metaData: {
                  cancelationDate: this.cancelationDate,
                  newOwner: this.newOwner
                }
              })
            })
          })
        }
      })
    },
    async prepareCustomTerms() {
      const allCustomTerms = await prismicService.getCustomTerms()

      const productsInBasket = this.checkoutServices.map(service => service.sku)
      if (allCustomTerms && allCustomTerms.length) {
        this.customTermsList = allCustomTerms
          .filter(customTerms =>
            productsInBasket.includes(customTerms.productSku)
          )
          .map(customTerms => {
            const { termsConfirmation: confirmText, termsText } = customTerms
            return {
              id: customTerms.productSku,
              confirmText,
              termsText,
              confirmed: false
            }
          })
        //if all products have custom terms, no base terms should be needed
        this.showBaseTerms =
          this.customTermsList.length !== productsInBasket.length
      }
    }
  }
}
</script>

<style lang="scss">
.checkout {
  height: 100%;

  .v-navigation-drawer__content {
    overflow-y: hidden;
  }

  .loader {
    text-align: center;
    padding-top: 48px;
  }

  &_header {
    padding-top: 50px;
    padding-bottom: 22px;
    background-color: white;
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    z-index: 3;

    .checkout-title {
      font-size: 2rem;
      color: var(--v-dark-base);
      margin-left: 48px;
      font-weight: lighter;
      text-align: left !important;

      @media screen and (min-width: 500px) {
        font-size: 3rem;
      }
    }

    .close-btn {
      position: absolute;
      top: 32px;
      right: 32px;
    }
  }

  &_main {
    margin-top: 150px;
    padding: 0 32px 22px;
    height: calc(100% - 336px);
    overflow: auto;

    @media screen and (min-width: 960px) {
      height: calc(100% - 270px);
      width: 720px;
    }

    .service-panel {
      &:not(.active) {
        margin-top: 16px;
        &:first-child {
          margin-top: 0;
        }
      }
      &.active {
        .service-panel-header {
          border-radius: 8px 8px 0 0;
          border-bottom: none;
        }
      }
      &-header {
        padding: 16px;
        border-radius: 8px;
        border: solid 1px var(--v-cloudy-base);
        background-color: var(--v-pale-two-base);
        color: var(--v-dark-base);

        &:before {
          padding: 16px;
          border-radius: 8px;
          border: solid 1px var(--v-cloudy-base);
          background-color: var(--v-pale-two-base);
        }

        .service-img {
          float: left;
          width: 40px;
          height: 40px;
        }

        .service-info {
          float: left;
          margin-left: 12px;
          line-height: 20px;

          .service-label {
            text-transform: uppercase;
            font-size: 0.75rem;
            font-weight: bold;
            color: var(--v-bluegrey-base);
          }

          .service-name {
            font-size: 1rem;
            font-weight: bold;
          }
        }

        .service-resume {
          margin-left: 12px;
          line-height: 20px;

          .service-vehicles {
            text-transform: uppercase;
            font-size: 0.75rem;
          }

          .service-totals {
            font-size: 1rem;
          }
        }

        .remove-title-header {
          font-size: 1rem;
          font-weight: bold;
          color: var(--v-slate-base);
        }
      }

      &-content {
        padding: 30px 0 18px;
        color: var(--v-dark-base);
        border: solid 1px var(--v-cloudy-base);

        .v-expansion-panel-content__wrap {
          padding: 0;
        }

        h2 {
          font-size: 1.25rem;
          color: var(--v-dark-base);
          padding: 0 16px;
        }

        .options {
          display: flex;
          align-items: center;
          padding: 0 16px;

          a {
            margin: 0 10px;
            font-size: 0.875rem;
            text-decoration: underline;
          }

          .start-spacer {
            width: 20px;
            height: 20px;
            margin-top: 20px;
            margin-left: 8px;
            border-top: 1px solid var(--v-cloudy-base);
            border-left: 1px solid var(--v-cloudy-base);
          }

          .vertical-spacer {
            border-left: 1px solid var(--v-cloudy-base);
            color: var(--v-cloudy-base);
            height: 16px;
            margin-top: 2px;
          }

          .horizontal-spacer {
            border-top: 1px solid var(--v-cloudy-base);
            flex-grow: 1;
          }

          .sort-by-label {
            font-size: 0.875rem;
            font-weight: bold;
            text-align: right;
            color: var(--v-slate-base);
            margin: 0 10px;
            &.mobile {
              width: 100px;
              text-align: left;
            }
          }

          .sort-by-items {
            max-width: 170px;
            height: 32px;
            font-size: 0.875rem;
            &.mobile {
              width: 100%;
              max-width: none;
            }

            .v-input {
              &__slot {
                min-height: 32px !important;
              }

              &__append-inner {
                margin-top: 0 !important;
              }
            }
          }
        }

        .service-table {
          overflow-x: hidden;

          .v-data-table-header {
            display: none;
          }

          &-row {
            border-bottom: thin dashed rgba(0, 0, 0, 0.12) !important;
            padding: 16px 0 12px;

            &.unsubscribe {
              background-color: #c530300c;
              border-radius: 0;
            }

            .service-description {
              font-size: 1rem;
              padding: 0 16px;
            }

            .row {
              margin: 0;

              div[class*='col-'] {
                font-size: 1rem;
                font-weight: bold;
                color: var(--v-dark-base);
                text-transform: uppercase;
                padding: 0 4px;

                &:first-child {
                  padding-left: 16px;
                  width: 52px;
                }

                &:last-child {
                  padding-right: 16px;
                }

                .v-input--checkbox {
                  margin-top: 0;

                  .v-input--selection-controls__input {
                    width: 16px;
                    height: 16px;
                    border-radius: 4px;
                    background-color: var(--v-pale-three-base);

                    i {
                      color: var(--v-pale-three-base);
                    }
                  }
                }

                .table-header {
                  font-size: 0.75rem;
                  font-weight: bold;
                  color: var(--v-bluegrey-base);
                  text-transform: uppercase;
                  height: 18px;
                }

                .row-total {
                  text-align: right;
                  text-transform: lowercase;

                  &.unsubscribe {
                    color: var(--v-reddish-base);
                  }

                  span {
                    font-size: 0.875rem;
                    font-weight: normal;
                  }
                }
              }
            }

            .data-end-message {
              font-size: 0.75rem;
              color: var(--v-bluegrey-base);
              margin-left: 52px;
              margin-top: 8px;
            }
          }
        }

        .show-all {
          padding: 0 16px;
          margin: 10px 0;

          .v-btn {
            width: 100%;
            border-radius: 4px;
            background-color: var(--v-ice-base) !important;
            color: var(--v-peacock-base);
            font-size: 0.6875rem;
            font-weight: bold;
            text-align: center;
            text-transform: uppercase;
            letter-spacing: normal;
          }
        }

        .total {
          padding: 0 16px;
          margin-top: 10px;

          .delete-order {
            letter-spacing: normal;
            text-transform: capitalize;
            text-decoration: underline;
            font-size: 0.75rem;
          }

          .total-label {
            font-size: 1rem;
            font-weight: bold;
            text-align: right;
            margin-right: 30px;
          }

          .total-value-label {
            font-size: 0.875rem;
          }

          .total-value {
            font-size: 1.125rem;
          }
        }

        .confirm-question {
          width: 512px;
          color: var(--v-reddish-base);
          font-size: 1rem;
          font-weight: bold;
          margin-left: 16px;
        }

        .select {
          position: relative;
          font-size: 0.875rem;
          .v-input__slot {
            border: 1px solid var(--v-pale-three-base) !important;
          }
          .v-icon {
            margin-top: 0 !important;
          }
        }

        .undo-removal {
          letter-spacing: normal;
          text-transform: capitalize;
          text-decoration: underline;
          font-size: 0.75rem;
        }

        .confirm-question {
          width: 512px;
          color: var(--v-reddish-base);
          font-size: 1rem;
          font-weight: bold;
          margin-left: 16px;
        }

        .select {
          position: relative;
          font-size: 0.875rem;
          .v-input__slot {
            border: 1px solid var(--v-pale-three-base) !important;
          }
          .v-icon {
            margin-top: 0 !important;
          }
        }

        .undo-removal {
          letter-spacing: normal;
          text-transform: capitalize;
          text-decoration: underline;
          font-size: 0.75rem;
        }

        .field-change-owner {
          position: relative;

          .v-input__slot {
            border: 1px solid var(--v-pale-three-base) !important;
          }
          .textfield {
            .placeholder {
              font-size: 0.75rem;
              color: var(--v-bluegrey-base);
            }
          }
        }
      }
    }

    .terms {
      border-radius: 8px;
      border: solid 1px var(--v-pale-three-base);
      background-color: var(--v-pale-two-base);
      margin-top: 32px;
      padding: 16px;
      font-size: 0.875rem;

      .check-terms {
        margin: 10px 0 0 0;

        label {
          font-size: 0.875rem;
          font-weight: bold;
          color: var(--v-dark-base);
        }
      }
    }
  }

  &_footer {
    padding: 20px 32px !important;
    background-color: white !important;
    border-top: 1px solid var(--v-cloudy-base) !important;

    .total-price {
      font-size: 1.25rem;
      font-weight: bold;
    }

    .alert-cdd-check {
      color: var(--v-reddish-base);
    }

    .v-btn {
      letter-spacing: normal;
      font-weight: bold;

      &.btn-continue {
        text-decoration: underline;
      }

      &.btn-pay {
        background-image: linear-gradient(
          to bottom,
          #266cab 0%,
          #01529c
        ) !important;
        &.v-btn--disabled:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined) {
          background-image: none !important;
          background-color: var(--v-cloudy-base) !important;
          color: white !important;
        }
      }

      &.btn-remove-vehicles {
        background-image: linear-gradient(
          to bottom,
          #c53030 0%,
          #9b2c2c
        ) !important;
        &.v-btn--disabled:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined) {
          background-image: none !important;
          background-color: var(--v-reddish-base) !important;
          color: white !important;
        }
      }
    }
  }
}
.vehiclevin-tooltip {
  min-height: 32px !important;
  border-radius: 16px !important;
  background: rgba(97, 97, 97, 0.9) !important;
  .vehiclevin-span {
    text-transform: uppercase;
    font-size: 0.75rem;
    font-weight: bold;
    line-height: normal;
    text-align: center;
    color: white;
  }
}
</style>
